import { useState, useEffect, useCallback } from "react";
import { Table, Form, InputGroup, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { useTerritorio } from "../../context/TerritorioContext";
import { useBacAuth } from "../../context/BacAuthContext";
import { getMrcvRegistros, eliminarMrcvRegistro, exportarMrcv } from "../api/mrcvAPI.js";
import { useOnlineStatus, cachearMrcv, leerMrcvCache } from "../../hooks/useSync";
import CrearMrcvModal from "../modal/CrearMrcvModal";
import VerMrcvModal from "../modal/VerMrcvModal";
import API from "../../axios.jsx";
import SelectorJornada, { coincideJornada } from "../jornadas/SelectorJornada";
import { exportarFiltradoBac } from "../api/bacSarampionAPI.js";
import { paramsJornada, nombreExcel, descargarBlob } from "../jornadas/exportarExcel";

const IconoOjo = (p) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const IconoCamara = (p) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2Z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);
const IconoEditar = (p) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z" />
  </svg>

);
const IconoEliminar = (p) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);
const BTN_ACCION = { background: "none", border: "none", cursor: "pointer", width: 30, height: 30, borderRadius: 7, display: "inline-flex", alignItems: "center", justifyContent: "center" };

const TH = { padding: "11px 14px", fontWeight: 600, whiteSpace: "nowrap", fontSize: 12 };
const TD = { padding: "10px 12px", verticalAlign: "middle", fontSize: 13 };
const REGISTROS_PAG = 10;
const GRUPO_LABEL = { "<6m": "< 6 meses", "<12m": "< 12 meses", "12-23m": "12-23 meses", "18-23m": "18-23 meses", "24-60m": "24-60 meses" };

export default function TablaMrcv({ puedeExportar = true, esAdmin = false, jornadas = [], filtroJornada = "", onFiltroJornada }) {
  const { data: ctx } = useTerritorio();
  const { usuario } = useBacAuth();
  const online = useOnlineStatus();
  const [listaBase, setLista] = useState([]);
  // Filtro de jornada (compartido con Visitas, Mapa y MRCV)
  const lista = listaBase.filter(r => coincideJornada(r.visita?.jornada_codigo, filtroJornada));
  const [cargando, setCargando] = useState(false);
  const [exportando, setExportando] = useState(false);

  const [busqueda, setBusqueda] = useState("");
  const [filtroGrupo, setFiltroGrupo] = useState("");
  const [filtroCarnet, setFiltroCarnet] = useState("");
  const [pagina, setPagina] = useState(1);
  const [tarjetaActiva, setTarjetaActiva] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [registroEditar, setRegistroEditar] = useState(null);
  const [showVer, setShowVer] = useState(false);
  const [registroVer, setRegistroVer] = useState(null);

  // El admin siempre puede editar — quien no es admin, solo si la
  // visita ligada quedó registrada a su propio nombre.
  // En una jornada que no está abierta (cerrada/programada) solo el admin corrige
  const jornadaAbierta = !filtroJornada || jornadas.some(j => coincideJornada(j.codigo, filtroJornada) && j.estado === "abierta");
  const puedeEditar = (r) => esAdmin || (jornadaAbierta && r.visita?.entrevistador && r.visita.entrevistador === usuario?.nombre);

  const cargar = useCallback(async () => {
    if (!ctx?.territorio) { setLista([]); return; }
    setCargando(true);
    if (online) {
      try {
        const res = await getMrcvRegistros({ territorio: ctx.territorio, comunidad: ctx.comunidad });
        const datos = res.data || [];
        setLista(datos);
        await cachearMrcv(ctx.territorio, ctx.comunidad, datos);
      } catch {
        const cache = await leerMrcvCache(ctx.territorio, ctx.comunidad);
        setLista(cache);
      }
    } else {
      const cache = await leerMrcvCache(ctx.territorio, ctx.comunidad);
      setLista(cache);
    }
    setCargando(false);
  }, [ctx?.territorio, ctx?.comunidad, online]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    const handler = () => cargar();
    window.addEventListener("sync-completado", handler);
    return () => window.removeEventListener("sync-completado", handler);
  }, [cargar]);

  useEffect(() => { setPagina(1); }, [busqueda, filtroGrupo, filtroCarnet, tarjetaActiva, filtroJornada]);

  const TARJETAS = [
    { id: "total",       label: "Total",                  bg: "rgba(255,255,255,0.18)", predicate: () => true },
    { id: "con_carnet",  label: "Con carné",            bg: "rgba(45,106,79,0.35)",   predicate: r => r.tiene_carnet === "SI" },
    { id: "sin_carnet",  label: "Sin carné",            bg: "rgba(179,38,30,0.35)",   predicate: r => r.tiene_carnet === "NO" },
    { id: "desplazados", label: "Desplazamiento",       bg: "rgba(183,121,31,0.35)",  predicate: r => r.condicion_desplazamiento === "SI" },
  ].map(t => ({ ...t, valor: lista.filter(t.predicate).length }));

  const tarjetaSel = TARJETAS.find(t => t.id === tarjetaActiva);

  const filtrados = lista.filter(r => {
    const q = busqueda.toLowerCase();
    const matchQ = !busqueda
      || r.nombre_nino?.toLowerCase().includes(q)
      || r.documento_nino?.includes(q)
      || r.visita?.codigo_vivienda?.toLowerCase().includes(q);
    const matchGrupo = !filtroGrupo || r.grupo_edad === filtroGrupo;
    const matchCarnet = !filtroCarnet || r.tiene_carnet === filtroCarnet;
    const matchTarjeta = !tarjetaSel || tarjetaSel.predicate(r);
    return matchQ && matchGrupo && matchCarnet && matchTarjeta;
  });

  const totalPags = Math.ceil(filtrados.length / REGISTROS_PAG);
  const paginados = filtrados.slice((pagina - 1) * REGISTROS_PAG, pagina * REGISTROS_PAG);

  const abrirEditar = (r) => { setRegistroEditar(r); setShowModal(true); };

  const eliminar = async (r) => {
    const confirmado = await Swal.fire({
      icon: "warning", title: `¿Eliminar el registro MRCV de ${r.nombre_nino}?`,
      showCancelButton: true, confirmButtonText: "Sí, eliminar", cancelButtonText: "Cancelar", confirmButtonColor: "#b3261e",
    });
    if (!confirmado.isConfirmed) return;
    try {
      await eliminarMrcvRegistro(r.id);
      await cargar();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "No se pudo eliminar", "error");
    }
  };

  // Excel SIEMPRE de la jornada en la que se está. Si la tabla tiene algún
  // filtro aplicado (búsqueda, estado, fechas, tarjeta…), sale solo lo filtrado.
  const exportar = async () => {
    setExportando(true);
    try {
      const params = paramsJornada(ctx, filtroJornada);
      const conFiltro = filtrados.length !== lista.length;
      const res = conFiltro
        ? await exportarFiltradoBac("mrcv", { params, ids: filtrados.map(r => r.id).filter(Boolean) })
        : await exportarMrcv(params);
      descargarBlob(res.data, nombreExcel("MRCV", ctx, jornadas, filtroJornada, conFiltro));
    } catch {
      Swal.fire("Error", "No se pudo exportar el Excel de MRCV", "error");
    } finally {
      setExportando(false);
    }
  };


  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      <div className="bac-hero" style={{ background: "linear-gradient(135deg, #16342a 0%, #1f4a3a 60%, #2d6a4f 100%)", borderRadius: 16, padding: "20px 24px", marginBottom: 20, color: "#fff" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div className="bac-hero-eyebrow" style={{ fontSize: 11, opacity: 0.75, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 3 }}>
              📋 MRCV — Monitoreo Rápido de Cobertura de Vacunación
            </div>
            <h5 style={{ margin: 0, fontWeight: 800, fontSize: "1.15rem" }}>Registro MRCV</h5>
            {ctx?.territorio && (
              <div className="bac-hero-lugar" style={{ marginTop: 5, fontSize: 13, opacity: 0.85 }}>📍 {ctx.territorio} {ctx.comunidad ? `· 🏘 ${ctx.comunidad}` : ""}</div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {TARJETAS.map(({ id, label, valor, bg }) => {
              const seleccionada = tarjetaActiva === id;
              return (
                <button key={id} className={`bac-stat${seleccionada ? " is-on" : ""}`} data-tono={id} onClick={() => setTarjetaActiva(seleccionada ? null : id)}
                  style={{ background: bg, borderRadius: 10, padding: "7px 14px", textAlign: "center", minWidth: 70, cursor: "pointer", border: seleccionada ? "2px solid #fff" : "2px solid transparent" }}>
                  <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#fff" }}>{valor}</div>
                  <div style={{ fontSize: 10, opacity: 0.9, color: "#fff" }}>{label}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <InputGroup style={{ maxWidth: 260, flex: 1 }}>
          <InputGroup.Text style={{ background: "#fff", border: "1.5px solid #dee2e6", borderRight: "none" }}>🔍</InputGroup.Text>
          <Form.Control placeholder="Niño, documento o código vivienda..." value={busqueda} onChange={e => setBusqueda(e.target.value)}
            style={{ fontSize: 13, border: "1.5px solid #dee2e6", borderLeft: "none" }} />
        </InputGroup>
        <SelectorJornada jornadas={jornadas} value={filtroJornada} onChange={onFiltroJornada} />

        <Form.Select value={filtroGrupo} onChange={e => setFiltroGrupo(e.target.value)} style={{ maxWidth: 170, fontSize: 13 }}>
          <option value="">Todos los grupos</option>
          {Object.entries(GRUPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Form.Select>
        <Form.Select value={filtroCarnet} onChange={e => setFiltroCarnet(e.target.value)} style={{ maxWidth: 150, fontSize: 13 }}>
          <option value="">Con y sin carné</option>
          <option value="SI">Con carné</option>
          <option value="NO">Sin carné</option>
        </Form.Select>

        {puedeExportar && (
          <Button variant="outline-secondary" onClick={exportar} disabled={exportando} style={{ fontSize: 13 }}
            title={filtrados.length !== lista.length ? "Descarga solo las filas que deja el filtro de la tabla" : "Descarga toda la jornada"}>
            {exportando ? "Exportando..." : filtrados.length !== lista.length ? `Exportar Excel (${filtrados.length} filtrados)` : "Exportar Excel de la jornada"}
          </Button>
        )}
      </div>

      {cargando ? <p className="text-muted">Cargando...</p> : (
        <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: "1px solid #e9ecef" }}>
          <div style={{ overflowX: "auto" }}>
            <Table hover style={{ marginBottom: 0 }}>
              <thead style={{ background: "#f8faf8" }}>
                <tr>
                  <th style={TH}>Niño/a</th>
                  <th style={TH}>Documento</th>
                  <th style={TH}>Fecha nacimiento</th>
                  <th style={TH}>Grupo edad</th>
                  <th style={TH}>Madre</th>
                  <th style={TH}>Vivienda</th>
                  <th style={TH}>Carné</th>
                  <th style={TH}>Fotos carné</th>
                  <th style={TH}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {paginados.map(r => (
                  <tr key={r.id}>
                    <td style={TD}>{r.nombre_nino}</td>
                    <td style={{ ...TD, fontFamily: "monospace", fontSize: 12 }}>{r.documento_nino || "—"}</td>
                    <td style={TD}>{r.fecha_nacimiento?.split?.("T")?.[0] || r.fecha_nacimiento}</td>
                    <td style={TD}><span className="badge bg-secondary">{GRUPO_LABEL[r.grupo_edad] || r.grupo_edad}</span></td>
                    <td style={TD}>{r.nombre_madre || "—"}</td>
                    <td style={{ ...TD, fontFamily: "monospace", fontSize: 12 }}>{r.visita?.codigo_vivienda}</td>
                    <td style={TD}>
                      {r.tiene_carnet === "SI"
                        ? <span className="badge bg-success">✓ Sí</span>
                        : <span className="badge bg-danger">✕ No</span>}
                    </td>
                    <td style={TD}>
                      {r.carnet_foto_a_url || r.carnet_foto_b_url || r.carnet_foto_paiweb_url ? (
                        <div className="d-flex flex-column gap-1">
                          {[
                            { url: r.carnet_foto_a_url, label: "Cara A" },
                            { url: r.carnet_foto_b_url, label: "Cara B" },
                            { url: r.carnet_foto_paiweb_url, label: "PAIWEB" },
                          ].map(({ url, label }) => (
                            <div className="d-flex align-items-center gap-1" key={label}>
                              <div style={{ width: 28, height: 28, borderRadius: 5, overflow: "hidden", border: "1px solid #dee2e6", flexShrink: 0, background: "#f5f6f0" }}>
                                {url ? (
                                  <img src={`${API.defaults.baseURL}${url}`} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                ) : (
                                  <div className="d-flex align-items-center justify-content-center h-100" style={{ fontSize: 9, color: "#adb5bd" }}>—</div>
                                )}
                              </div>
                              <span style={{ fontSize: 10, color: "#6c757d", minWidth: 40 }}>{label}</span>
                              {url ? (
                                <button onClick={() => window.open(`${API.defaults.baseURL}${url}`, "_blank")}
                                  className="btn btn-outline-secondary btn-sm py-0 px-2" style={{ fontSize: 10 }}>
                                  Ver
                                </button>
                              ) : (
                                <span className="badge bg-light text-secondary border" style={{ fontSize: 9.5 }}>No</span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted" style={{ fontSize: 11 }}>Sin fotos</span>
                      )}
                    </td>
                    <td style={{ ...TD, whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => { setRegistroVer(r); setShowVer(true); }} title="Ver detalle"
                          style={{ ...BTN_ACCION, color: "#16342a" }}>
                          <IconoOjo />
                        </button>
                        {r.carnet_foto_a_url && (
                          <button onClick={() => window.open(`${API.defaults.baseURL}${r.carnet_foto_a_url}`, "_blank")} title="Ver foto del carné"
                            style={{ ...BTN_ACCION, color: "#0C447C" }}>

                            <IconoCamara />
                          </button>
                        )}
                        {puedeEditar(r) && (
                          <button onClick={() => abrirEditar(r)} title="Editar" style={{ ...BTN_ACCION, color: "#0C447C" }}>
                            <IconoEditar />
                          </button>
                        )}
                        {esAdmin && (
                          <button onClick={() => eliminar(r)} title="Eliminar" style={{ ...BTN_ACCION, color: "#b3261e" }}>
                            <IconoEliminar />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!paginados.length && (
                  <tr><td colSpan={9} style={{ ...TD, textAlign: "center", color: "#8a94a6", fontStyle: "italic" }}>Sin registros de MRCV todavía</td></tr>
                )}
              </tbody>
            </Table>
          </div>

          <div style={{ padding: "10px 20px", background: "#f8faf8", borderTop: "1px solid #e9ecef", fontSize: 12, color: "#6c757d", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <span>Mostrando <strong>{paginados.length}</strong> de <strong>{filtrados.length}</strong> · Total: <strong>{lista.length}</strong></span>
            {totalPags > 1 && (
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1} style={{ border: "1px solid #dee2e6", background: "#fff", borderRadius: 6, padding: "3px 10px", fontSize: 12 }}>◀</button>
                <span style={{ fontWeight: 700 }}>{pagina}/{totalPags}</span>
                <button onClick={() => setPagina(p => Math.min(totalPags, p + 1))} disabled={pagina === totalPags} style={{ border: "1px solid #dee2e6", background: "#fff", borderRadius: 6, padding: "3px 10px", fontSize: 12 }}>▶</button>
              </div>
            )}
          </div>
        </div>
      )}

      <CrearMrcvModal
        show={showModal}
        registroEditar={registroEditar}
        onClose={() => { setShowModal(false); setRegistroEditar(null); }}
        onGuardado={cargar}
      />
      <VerMrcvModal
        show={showVer}
        registro={registroVer}
        onClose={() => { setShowVer(false); setRegistroVer(null); }}
      />
    </div>
  );
}